import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

export type Lang = "en" | "pl";

interface LanguageContextType {
  lang: Lang;
  setLang: (l: Lang) => void;
}

const LanguageContext = createContext<LanguageContextType>({
  lang: "en",
  setLang: () => {},
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const location = useLocation();

  const detect = (): Lang => {
    if (location.pathname.startsWith("/pl")) return "pl";
    const saved = localStorage.getItem("cw-lang") as Lang | null;
    return saved ?? "en";
  };

  const [lang, setLangState] = useState<Lang>(detect);

  // Sync language when route changes
  useEffect(() => {
    const detected = location.pathname.startsWith("/pl") ? "pl" : "en";
    setLangState(detected);
    document.documentElement.lang = detected;
  }, [location.pathname]);

  const setLang = (l: Lang) => {
    setLangState(l);
    localStorage.setItem("cw-lang", l);
    document.documentElement.lang = l;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  return useContext(LanguageContext);
}
